import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import Badge from "../ui/badge/Badge";
import { BadgeCheck, Ban, Link, ThumbsUp } from 'lucide-react';
import MoreButton from '../common/MoreButton';
import api from "../../services/api";
import authService from "../../services/authService";
import Pagination from "../ui/pagination/Pagination";
import ComponentCard from "../common/ComponentCard";
import Modal from '../common/Modal';
import Alert from '../ui/alert/Alert';
import { useNavigate } from "react-router";

// Definição da interface da afiliação
interface Affiliate {
    id: string;
    affiliateId?: string;
    companyId?: string;
    mainCompanyId?: string;
    email: string;
    company?: {
        id: string;
        name: string;
    };
    product?: {
        id: string;
        name: string;
    };
    contractEnd?: string;
    contractStart?: string;
    contractDuration?: string;
    expiresAt?: string;
    sentAt?: string;
    acceptedAt?: string;
    percentage: number | string;
    productId: string;
    participantInvitationId?: string;
    type: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    token?: string;
}

export default function CompanyCoproducerTable() {
    const companyId = authService.getCompanyId();
    const navigate = useNavigate();
    // Dados fixos para a tabela (posteriormente serão substituídos por dados da API)
    const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
    const [receivedInvitations, setReceivedInvitations] = useState<Affiliate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Estados para o modal de exclusão
    const [revokeModalOpen, setRevokeModalOpen] = useState<boolean>(false);
    const [acceptModalOpen, setAcceptModalOpen] = useState<boolean>(false);
    const [acceptRevocationModalOpen, setAcceptRevocationModalOpen] = useState<boolean>(false);
    const [rejectRevocationModalOpen, setRejectRevocationModalOpen] = useState<boolean>(false);
    const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);
    const [revokeLoading, setRevokeLoading] = useState<boolean>(false);
    const [acceptLoading, setAcceptLoading] = useState<boolean>(false);
    const [acceptRevocationLoading, setAcceptRevocationLoading] = useState<boolean>(false);
    const [rejectInvitationModalOpen, setRejectInvitationModalOpen] = useState<boolean>(false);
    const [rejectInvitationLoading, setRejectInvitationLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchCompanyAffiliates = async () => {
            try {
                setLoading(true);
                setError(null);

                // Buscando os afiliados do produto
                const response = await api.get(`/api/product-participants/company/${companyId}/participations`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.success) {
                    // A API retorna um objeto com propriedades all, coproductions e affiliations
                    const responseData = response.data.data;

                    // Usamos o array coproductions que é o que queremos mostrar nesta tabela
                    if (responseData.affiliations && Array.isArray(responseData.affiliations)) {
                        setAffiliates(responseData.affiliations);
                    } else if (responseData.all && Array.isArray(responseData.all)) {
                        // Fallback para o array 'all' se coproductions não existir
                        setAffiliates(responseData.all);
                    } else {
                        setAffiliates([]);
                    }
                } else {
                    setAffiliates([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar afiliados do produto:", err);
                setError(err.message || "Erro ao carregar afiliados do produto");
            } finally {
                setLoading(false);
            }
        };

        if (companyId) {
            fetchCompanyAffiliates();
        }
    }, [companyId]);

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => {
        const fetchReceivedInvitation = async () => {
            try {
                setLoading(true);
                setError(null);

                // Buscando os convites recebidos
                const response = await api.get(`/api/product-participants/company/${companyId}/received-invitations`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.success) {
                    const invitations = response.data.data || [];
                    setReceivedInvitations(invitations);
                } else {
                    setReceivedInvitations([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar convites:", err);
                setError(err.message || "Erro ao carregar convites enviados");
            } finally {
                setLoading(false);
            }
        };

        if (companyId) {
            fetchReceivedInvitation();
        } else {
            console.log("CompanyId não encontrado. Não é possível buscar convites.");
        }
    }, [companyId]);

    // Cálculo para paginação dos convites recebidos
    const indexOfLastInvitation = currentPage * itemsPerPage;
    const indexOfFirstInvitation = indexOfLastInvitation - itemsPerPage;
    const currentInvitations = receivedInvitations.slice(indexOfFirstInvitation, indexOfLastInvitation);
    const totalInvitationPages = Math.ceil(receivedInvitations.length / itemsPerPage);

    // Cálculo para paginação dos afiliados
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = affiliates.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(affiliates.length / itemsPerPage);

    // Função para aceitar um convite de co-produção
    const handleAcceptCoproduction = async () => {
        if (!selectedInvitationId) return;

        try {
            // Buscar o convite selecionado nos recebidos
            const invitation = receivedInvitations.find(inv => inv.id === selectedInvitationId);

            if (!invitation || !invitation.token) {
                throw new Error("Token do convite não encontrado");
            }

            setAcceptLoading(true);

            // Chamada para a API para aceitar o convite
            const response = await api.post(
                `/api/product-participants/accept-invitation/${invitation.token}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar resposta da API
            if (response.data && response.data.success) {
                // Atualizar as listas de convites e afiliados
                const updatedInvitations = receivedInvitations.filter(
                    inv => inv.id !== selectedInvitationId
                );
                setReceivedInvitations(updatedInvitations);

                // Atualizar a lista de afiliados (poderia recarregar da API também)
                if (response.data.participant) {
                    setAffiliates([...affiliates, response.data.participant]);
                }

                // Mensagem de sucesso
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Convite de afiliação aceito com sucesso."
                });
            } else {
                throw new Error(response.data.error || "Erro ao aceitar convite");
            }
        } catch (err: any) {
            console.error("Erro ao aceitar convite de afiliação:", err);

            // Mensagem de erro
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: err.message || "Ocorreu um erro ao aceitar o convite de afiliação."
            });
        } finally {
            // Fechar o modal e limpar o estado
            setAcceptLoading(false);
            setAcceptModalOpen(false);
            setSelectedInvitationId(null);

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    const handleRevokeAffiliate = async () => {
        if (!selectedInvitationId) return;

        try {
            // Buscar a afiliação selecionada
            const affiliate = affiliates.find(aff => aff.id === selectedInvitationId);

            if (!affiliate) {
                throw new Error("Afiliação não encontrada");
            }

            setRevokeLoading(true);

            // Chamada para a API para revogar a afiliação
            const response = await api.post(
                `/api/product-participants/revoke/${affiliate.id}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar resposta da API
            if (response.data && response.data.success) {
                // Atualizar a lista de afiliados, removendo a afiliação cancelada
                const updatedAffiliates = affiliates.filter(
                    aff => aff.id !== selectedInvitationId
                );
                setAffiliates(updatedAffiliates);

                // Mensagem de sucesso
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Afiliação cancelada com sucesso."
                });
            } else {
                throw new Error(response.data.error || "Erro ao cancelar afiliação");
            }
        } catch (err: any) {
            console.error("Erro ao cancelar afiliação:", err);

            // Mensagem de erro
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: err.message || "Ocorreu um erro ao cancelar a afiliação."
            });
        } finally {
            // Fechar o modal e limpar o estado
            setRevokeLoading(false);
            setRevokeModalOpen(false);
            setSelectedInvitationId(null);

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    const handleRejectInvitation = async () => {
        if (!selectedInvitationId) return;

        try {
            // Buscar o convite selecionado
            const invitation = receivedInvitations.find(inv => inv.id === selectedInvitationId);

            if (!invitation) {
                throw new Error("Convite não encontrado");
            }

            setRejectInvitationLoading(true);

            // Chamada para a API para recusar o convite
            const response = await api.post(
                `/api/product-participants/cancel-invitation/${invitation.token}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar resposta da API
            if (response.data && response.data.success) {
                // Atualizar a lista de convites, removendo o convite recusado
                const updatedInvitations = receivedInvitations.filter(
                    inv => inv.id !== selectedInvitationId
                );
                setReceivedInvitations(updatedInvitations);

                // Mensagem de sucesso
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Convite recusado com sucesso."
                });
            } else {
                throw new Error(response.data.error || "Erro ao recusar convite");
            }
        } catch (err: any) {
            console.error("Erro ao recusar convite:", err);

            // Mensagem de erro
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: err.message || "Ocorreu um erro ao recusar o convite."
            });
        } finally {
            // Fechar o modal e limpar o estado
            setRejectInvitationLoading(false);
            setRejectInvitationModalOpen(false);
            setSelectedInvitationId(null);

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    const handleAcceptRevocation = async () => {
        if (!selectedInvitationId) return;

        try {
            // Buscar o convite selecionado
            const invitation = receivedInvitations.find(inv => inv.id === selectedInvitationId);

            if (!invitation) {
                throw new Error("Convite não encontrado");
            }

            setAcceptRevocationLoading(true);

            // Chamada para a API para recusar o convite
            const response = await api.post(
                `/api/product-participants/accept-revocation/${invitation.token}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar resposta da API
            if (response.data && response.data.success) {
                // Atualizar a lista de convites, removendo o convite recusado
                const updatedInvitations = receivedInvitations.filter(
                    inv => inv.id !== selectedInvitationId
                );
                setReceivedInvitations(updatedInvitations);

                // Mensagem de sucesso
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Convite recusado com sucesso."
                });
            } else {
                throw new Error(response.data.error || "Erro ao recusar convite");
            }
        } catch (err: any) {
            console.error("Erro ao recusar convite:", err);

            // Mensagem de erro
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: err.message || "Ocorreu um erro ao recusar o convite."
            });
        } finally {
            // Fechar o modal e limpar o estado
            setAcceptRevocationLoading(false);
            setAcceptRevocationModalOpen(false);
            setSelectedInvitationId(null);

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    // Função para editar uma afiliação
    const handleAffiliateLinks = (affId: string) => {
        navigate(`/products/affiliate/${affId}/links`);
    };

    const openRejectInvitationModal = (invitationId: string) => {
        setSelectedInvitationId(invitationId);
        setRejectInvitationModalOpen(true);
    };

    const openRevokeModal = (coproductionId: string) => {
        setSelectedInvitationId(coproductionId);
        setRevokeModalOpen(true);
    };

    const openAcceptInvitationModal = (invitationId: string) => {
        setSelectedInvitationId(invitationId);
        setAcceptModalOpen(true);
    };

    const openAcceptRevocationModal = (invitationId: string) => {
        setSelectedInvitationId(invitationId);
        setAcceptRevocationModalOpen(true);
    };

    const openRejectRevocationModal = (invitationId: string) => {
        setSelectedInvitationId(invitationId);
        setRejectRevocationModalOpen(true);
    };

    // Função para renderizar a tabela de convites recebidos (só exibida se houver convites)
    const renderInvitationsTable = () => (
        <div className="overflow-hidden rounded-xl">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Produto
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Comissão
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Duração do contrato
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Expiração do convite
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                            >
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {currentInvitations.map((invitation) => (
                            <TableRow key={invitation.id}>
                                <TableCell className="px-5 py-4 text-start">
                                    <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                        {new Date(invitation.sentAt || invitation.createdAt).toLocaleDateString()}
                                    </span>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    <div>
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                            {invitation.product?.name}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {invitation.percentage}%
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {invitation.contractDuration ? `${invitation.contractDuration} dias` : "Eterno"}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {invitation.expiresAt ? new Date(invitation.expiresAt).toLocaleDateString() : "Eterno"}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    <Badge
                                        size="sm"
                                        color={invitation.status === "pending" ? "warning" : (invitation.status === "accepted" ? "success" : "error")}
                                    >
                                        {invitation.status === "pending" ? "Pendente" : (invitation.status === "accepted" ? "Aceito" : "Rejeitado")}
                                    </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                    <div className="flex justify-end gap-2 relative">
                                        {invitation.type === "revocation" ? (
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Cancelar contrato",
                                                        onClick: () => openAcceptRevocationModal(invitation.id),
                                                        icon: <ThumbsUp className="size-4" />,
                                                        className: "text-green-600 dark:text-green-400"
                                                    },
                                                    {
                                                        label: "Recusar cancelamento",
                                                        onClick: () => openRejectRevocationModal(invitation.id),
                                                        icon: <Ban className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        ) : invitation.status === "pending" ? (
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Aceitar",
                                                        onClick: () => openAcceptInvitationModal(invitation.id),
                                                        icon: <ThumbsUp className="size-4" />,
                                                        className: "text-green-600 dark:text-green-400"
                                                    },
                                                    {
                                                        label: "Recusar",
                                                        onClick: () => openRejectInvitationModal(invitation.id),
                                                        icon: <Ban className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        ) : null}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {totalInvitationPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalInvitationPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    // Função para renderizar a tabela de afiliados
    const renderAffiliatesTable = () => (
        <div className="overflow-hidden rounded-xl">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data de início
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Produto
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Comissão
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Duração
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                            >
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {affiliates.length > 0 ? (
                            currentItems.map((aff) => (
                                <TableRow key={aff.id}>
                                    <TableCell className="px-5 py-4 text-start">
                                        <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                            {new Date(aff.acceptedAt || aff.createdAt).toLocaleDateString()}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {aff.product?.name}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {aff.percentage}%
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {aff.contractDuration ? `${aff.contractDuration} dias` : "Eterno"}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={
                                                aff.status === "active" ? "success" :
                                                    aff.status === "pending_revocation" ? "info" :
                                                        "error"
                                            }
                                        >
                                            {
                                                aff.status === "active" ? "Ativo" :
                                                    aff.status === "pending_revocation" ? "Cancelamento pendente" :
                                                        "Inativo"
                                            }
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                        <div className="flex justify-end gap-2 relative">
                                            {aff.status !== "pending_revocation" && (
                                                <MoreButton
                                                    options={[
                                                        {
                                                            label: "Links",
                                                            onClick: () => handleAffiliateLinks(aff.id),
                                                            icon: <Link className="size-4" />
                                                        }, {
                                                            label: "Encerrar",
                                                            onClick: () => openRevokeModal(aff.id),
                                                            icon: <Ban className="size-4" />,
                                                            className: "text-red-600 dark:text-red-400"
                                                        }
                                                    ]}
                                                />
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 dark:text-gray-400">
                                    Nenhuma afiliação ativa.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    // Renderização principal
    if (loading) {
        return <div className="p-4 text-center dark:text-gray-400">Carregando afiliados...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    return (
        <div>
            {/* Campo de busca */}
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Exibir tabelas */}
            <div className="space-y-8">
                {receivedInvitations.length > 0 && receivedInvitations[0].type === "affiliate" && (
                    <ComponentCard title="Convites recebidos">
                        {renderInvitationsTable()}
                        {/* Modal de confirmação de recusa */}
                        <Modal
                            isOpen={rejectInvitationModalOpen}
                            onClose={() => setRejectInvitationModalOpen(false)}
                            size="sm"
                            onConfirm={handleRejectInvitation}
                            confirmText="Recusar"
                            cancelText="Voltar"
                            confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                            confirmLoading={rejectInvitationLoading}
                        >
                            <div className="flex flex-col items-center text-center py-4">
                                <Ban className="h-16 w-16 text-red-500 mb-4" />
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Recusar convite de afiliação
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                    Não será possível reverter esta ação!
                                </p>
                            </div>
                        </Modal>
                        {/* Modal de aceite da co-produção */}
                        <Modal
                            isOpen={acceptModalOpen}
                            onClose={() => setAcceptModalOpen(false)}
                            size="sm"
                            onConfirm={handleAcceptCoproduction}
                            confirmText="Aceitar"
                            cancelText="Voltar"
                            confirmButtonClass="bg-green-500 hover:bg-green-600 text-white"
                            confirmLoading={acceptLoading}
                        >
                            <div className="flex flex-col items-center text-center py-4">
                                <BadgeCheck className="h-16 w-16 text-green-500 mb-4" />
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Confirmar aceite do convite de afiliação
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                    Ao aceitar, você concorda com os termos e percentual de comissão definidos no convite.
                                </p>
                            </div>
                        </Modal>
                        {/* Modal de confirmação de revocação */}
                        <Modal
                            isOpen={acceptRevocationModalOpen}
                            onClose={() => setAcceptRevocationModalOpen(false)}
                            size="sm"
                            onConfirm={handleAcceptRevocation}
                            confirmText="Confirmar"
                            cancelText="Voltar"
                            confirmButtonClass="bg-green-500 hover:bg-green-600 text-white"
                            confirmLoading={acceptRevocationLoading}
                        >
                            <div className="flex flex-col items-center text-center py-4">
                                <BadgeCheck className="h-16 w-16 text-green-500 mb-4" />
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Confirmar cancelamento da afiliação
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                    Ao confirmar, o contrato será cancelado e o afiliado será notificado.
                                </p>
                            </div>
                        </Modal>
                        {/* Modal de rejeição de revocação */}
                        <Modal
                            isOpen={rejectRevocationModalOpen}
                            onClose={() => setRejectRevocationModalOpen(false)}
                            size="sm"
                            onConfirm={handleRejectInvitation}
                            confirmText="Recusar"
                            cancelText="Voltar"
                            confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                            confirmLoading={rejectInvitationLoading}
                        >
                            <div className="flex flex-col items-center text-center py-4">
                                <Ban className="h-16 w-16 text-red-500 mb-4" />
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Deseja recusar o cancelamento da afiliação?
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                    Ao recusar, a afiliação permanecerá ativa e o afiliado será notificado.
                                </p>
                            </div>
                        </Modal>
                    </ComponentCard>
                )}
                <ComponentCard title={affiliates.length > 0 ? "Minhas Afiliações" : "Afiliações"}>
                    {renderAffiliatesTable()}
                    {/* Modal de confirmação de exclusão */}
                    <Modal
                        isOpen={revokeModalOpen}
                        onClose={() => setRevokeModalOpen(false)}
                        size="sm"
                        onConfirm={handleRevokeAffiliate}
                        confirmText="Encerrar"
                        cancelText="Voltar"
                        confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                        confirmLoading={revokeLoading}
                    >
                        <div className="flex flex-col items-center text-center py-4">
                            <Ban className="h-16 w-16 text-red-500 mb-4" />
                            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Deseja realmente encerrar a afiliação?
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                                Não será possível reverter esta ação!
                            </p>
                        </div>
                    </Modal>
                </ComponentCard>
            </div>

            {/* Alertas */}
            {alertInfo && alertInfo.show && (
                <div className="mt-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}
        </div>
    );
}